import React, { useState, useContext } from 'react';
import { Box, Typography, Avatar, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { RoomOutlined, EditOutlined, DeleteOutlined, WarningAmberOutlined, AddOutlined } from '@mui/icons-material';
import { v4 as uuid } from 'uuid';
import { PageCtx, CtxMenuCtx, DragCtx } from '../contexts';
import { SCHEDULE_COLOR } from '../constants';
import { applyDragPreview } from '../utils';
import { api } from '../../../../hooks/useApi';
import SessionDrawer from '../../eventDetailsTab/SessionDrawer';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getInitials(name = '') {
  return name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}
function getHue(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
}

function to12h(time) {
  if (!time) return '';
  const [hStr, mStr] = time.split(':');
  let h = parseInt(hStr, 10);
  const m = mStr || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

function formatDayLabel(label) {
  if (!label) return null;
  const iso = label.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(`${iso[1]}-${iso[2]}-${iso[3]}T00:00:00`);
    if (!isNaN(d)) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return label;
}

function groupByDayLabel(schedule) {
  const map = new Map();
  for (const item of schedule) {
    const key = item.dayLabel || `day-${item.dayNumber || 1}`;
    if (!map.has(key)) map.set(key, { key, tabIndex: map.size + 1, dayLabel: item.dayLabel || null, items: [] });
    map.get(key).items.push(item);
  }
  const groups = Array.from(map.values());
  for (const g of groups) {
    g.items.sort((a, b) =>
      (a.startTime || '').localeCompare(b.startTime || '') ||
      (a.order || 0) - (b.order || 0)
    );
  }
  return groups;
}

// ─── Breakpoint-aware prop reader ────────────────────────────────────────────
function getSchedProp(el, bp, key) {
  if (!bp || bp === 'desktop') return el[key];
  return el.responsive?.[bp]?.[key] ?? el[key];
}

// Resolve a speakerRef — backend may have already resolved it to a full object,
// or it may still be an ID/name string. Handle both.
function resolveSpeaker(speakerRef, allSpeakers) {
  if (speakerRef && typeof speakerRef === 'object') return speakerRef;
  return allSpeakers.find(
    s => s.id === speakerRef || s._id === speakerRef || s.name === speakerRef
  );
}

// ─── Speaker circle — no label, name shows in native tooltip ─────────────────
function SpeakerCircle({ speakerRef, allSpeakers }) {
  const speaker = resolveSpeaker(speakerRef, allSpeakers);
  const name = speaker?.name || (typeof speakerRef === 'string' ? speakerRef : '') || '?';
  const hue = getHue(name);
  const initials = getInitials(name);

  return speaker?.avatar ? (
    <Box
      component="img" src={speaker.avatar} alt={name} title={name}
      sx={{
        width: 36, height: 36, borderRadius: '50%', objectFit: 'cover',
        border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.12)', display: 'block',
        cursor: 'default', flexShrink: 0
      }}
    />
  ) : (
    <Avatar title={name} sx={{
      width: 36, height: 36, fontSize: '0.75rem', fontWeight: 700,
      background: `hsl(${hue},58%,46%)`,
      border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
      cursor: 'default', flexShrink: 0,
    }}>
      {initials}
    </Avatar>
  );
}

// ─── Single schedule row ──────────────────────────────────────────────────────
function ScheduleRow({ item, el, allSpeakers, bannerUrl, isLast, onEdit, onDelete, isMobile = false }) {
  const [hovered, setHovered] = useState(false);
  const bp2 = isMobile ? 'mobile' : 'desktop'; // use passed isMobile to resolve bp-aware props
  const primaryColor = getSchedProp(el, bp2, 'primaryColor') || '#1075BA';
  const secondaryColor = getSchedProp(el, bp2, 'secondaryColor') || '#6b7280';
  const dividerColor = getSchedProp(el, bp2, 'dividerColor') || '#f0f4f8';
  const timeFontSize = getSchedProp(el, bp2, 'timeFontSize') || '22px';
  const titleFontSize = getSchedProp(el, bp2, 'titleFontSize') || '25px';
  const descFontSize = getSchedProp(el, bp2, 'descFontSize') || '13px';
  const showSpeakers = getSchedProp(el, bp2, 'showSpeakers') !== false;
  const showBanner = getSchedProp(el, bp2, 'showBanner') !== false;
  const showDescription = getSchedProp(el, bp2, 'showDescription') !== false;
  const showLocation = getSchedProp(el, bp2, 'showLocation') !== false;

  const speakerRefs = item.speakers || [];
  const formattedDate = formatDayLabel(item.dayLabel);
  const MAX_VISIBLE = 4;
  const visibleRefs = speakerRefs.slice(0, MAX_VISIBLE);
  const overflow = speakerRefs.length - MAX_VISIBLE;

  // ── Shared sub-blocks ──────────────────────────────────────────────────────
  const bannerImg = showBanner && (item.posterUrl || bannerUrl) ? (
    <Box component="img" src={item.posterUrl || bannerUrl} alt=""
      sx={{ width: 96, height: 72, objectFit: 'cover', borderRadius: '8px', flexShrink: 0, display: 'block' }} />
  ) : null;

  const speakerBlock = showSpeakers && speakerRefs.length > 0 ? (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {visibleRefs.map((ref, i) => (
        <Box key={i} sx={{ ml: i > 0 ? '-10px' : 0, zIndex: MAX_VISIBLE - i }}>
          <SpeakerCircle speakerRef={ref} allSpeakers={allSpeakers} />
        </Box>
      ))}
      {overflow > 0 && (
        <Typography sx={{ ml: 1, fontSize: '12px', fontWeight: 600, color: secondaryColor, whiteSpace: 'nowrap', flexShrink: 0 }}>
          and {overflow} other{overflow > 1 ? 's' : ''}
        </Typography>
      )}
    </Box>
  ) : null;

  // Action buttons are absolutely positioned so they never affect row layout
  const actionBlock = (
    <Box onClick={e => e.stopPropagation()}
      sx={{
        position: 'absolute', top: 16, right: 0, zIndex: 10,
        display: 'flex', flexDirection: 'column', gap: 0.5,
        opacity: hovered ? 1 : 0, transition: 'opacity 0.15s',
      }}>
      <IconButton size="small" onClick={() => onEdit(item)}
        sx={{
          background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)', p: 0.9,
          color: '#b0bac8', '&:hover': { background: '#fff', color: SCHEDULE_COLOR }
        }}>
        <EditOutlined sx={{ fontSize: 22 }} />
      </IconButton>
      <IconButton size="small" onClick={() => onDelete(item)}
        sx={{
          background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)', p: 0.9,
          color: '#b0bac8', '&:hover': { background: '#fff', color: '#d32f2f' }
        }}>
        <DeleteOutlined sx={{ fontSize: 22 }} />
      </IconButton>
    </Box>
  );

  // ── Mobile: single column ───────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          position: 'relative',
          display: 'flex', flexDirection: 'column', gap: 0,
          py: 2.5, borderBottom: isLast ? 'none' : `1px solid ${dividerColor}`,
          transition: 'background 0.15s',
          '&:hover': { background: alpha(SCHEDULE_COLOR, 0.025) },
        }}>
        {/* 1. Time + date — above title, no gap below */}
        {(item.startTime || formattedDate) && (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            {item.startTime && (
              <Typography sx={{ fontSize: '16px', fontWeight: 800, color: primaryColor, lineHeight: 1 }}>
                {to12h(item.startTime)}
              </Typography>
            )}
            {formattedDate && (
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: secondaryColor, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {formattedDate}
              </Typography>
            )}
          </Box>
        )}
        {/* 2. Title full width + actions */}
        {/* 2. Title full width */}
        <Typography sx={{ fontSize: titleFontSize, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.3, mb: 1, pr: '10px' }}>
          {item.title}
        </Typography>
        {/* actions absolutely positioned */}
        {actionBlock}
        {/* 3. Banner — full width */}
        {bannerImg && (
          <Box sx={{ mb: 1 }}>
            <Box component="img" src={item.posterUrl || bannerUrl} alt=""
              sx={{ width: '100%', aspectRatio: '96/72', objectFit: 'cover', borderRadius: '8px', display: 'block' }} />
          </Box>
        )}
        {/* 4. Description */}
        {showDescription && item.description && (
          <Typography sx={{ fontSize: descFontSize, color: secondaryColor, lineHeight: 1.6, mb: 0.75 }}>
            {item.description}
          </Typography>
        )}
        {/* 5. Location */}
        {showLocation && item.location && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.75 }}>
            <RoomOutlined sx={{ fontSize: 13, color: alpha(secondaryColor, 0.65), flexShrink: 0 }} />
            <Typography sx={{ fontSize: '11.5px', color: alpha(secondaryColor, 0.75), fontStyle: 'italic' }}>
              {item.location}
            </Typography>
          </Box>
        )}
        {/* 6. Speakers */}
        {speakerBlock}
      </Box>
    );
  }

  // ── Desktop / tablet: 4-column grid ────────────────────────────────────────
  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '110px 1fr',
        gap: '20px',
        alignItems: 'flex-start',
        py: 3,
        borderBottom: isLast ? 'none' : `1px solid ${dividerColor}`,
        transition: 'background 0.15s',
        '&:hover': { background: alpha(SCHEDULE_COLOR, 0.025) },
      }}>
      {/* ── Left: time + date ── */}
      <Box sx={{ flexShrink: 0 }}>
        {item.startTime && (
          <Typography sx={{ fontSize: timeFontSize, fontWeight: 800, color: primaryColor, lineHeight: 1 }}>
            {to12h(item.startTime)}
          </Typography>
        )}
        {formattedDate && (
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: secondaryColor, mt: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {formattedDate}
          </Typography>
        )}
      </Box>
      {/* ── Center: banner + text ── */}
      <Box sx={{ minWidth: 0, display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
        {bannerImg}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: titleFontSize, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.3, mb: item.description ? 0.5 : 0 }}>
            {item.title}
          </Typography>
          {showDescription && item.description && (
            <Typography sx={{ fontSize: descFontSize, color: secondaryColor, lineHeight: 1.6 }}>
              {item.description}
            </Typography>
          )}
          {showLocation && item.location && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.5 }}>
              <RoomOutlined sx={{ fontSize: 13, color: alpha(secondaryColor, 0.65), flexShrink: 0 }} />
              <Typography sx={{ fontSize: '11.5px', color: alpha(secondaryColor, 0.75), fontStyle: 'italic' }}>
                {item.location}
              </Typography>
            </Box>
          )}
        </Box>
        {/* ── Speakers ── */}
        {speakerBlock || <Box />}
      </Box>

      {/* actions are position:absolute */}
      {actionBlock}
    </Box>
  );
}

// ─── Day tab — single line "Day 1 (May 12)" ─────────────────────────────────
function DayTab({ group, active, onClick, totalTabs, primaryColor = SCHEDULE_COLOR, isMobile = false }) {
  const dateFormatted = formatDayLabel(group.dayLabel);
  const label = dateFormatted
    ? `Day ${group.tabIndex} (${dateFormatted})`
    : `Day ${group.tabIndex}`;

  return (
    <Box
      onClick={onClick}
      sx={{
        flex: isMobile ? '0 0 auto' : 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        py: 1.5, px: isMobile ? 2 : 1.5, cursor: 'pointer',
        borderBottom: active ? `3px solid ${primaryColor}` : '3px solid transparent',
        background: active ? alpha(primaryColor, 0.04) : 'transparent',
        transition: 'all 0.18s',
        '&:hover': { background: alpha(primaryColor, 0.06) },
      }}
    >
      <Typography sx={{
        fontSize: totalTabs > 3 ? '0.85rem' : '1rem',
        fontWeight: 700,
        color: active ? primaryColor : '#5a6474',
        textAlign: 'center', whiteSpace: 'nowrap',
        transition: 'color 0.18s',
      }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── ScheduleElement ──────────────────────────────────────────────────────────
export function ScheduleElement({ el, selected, onSelect, onDragStart }) {
  const { open: openMenu } = useContext(CtxMenuCtx);
  const { schedule = [], speakers = [], eventBannerUrl, onScheduleUpdate, onSpeakersUpdate, eventId } = useContext(PageCtx);
  const bp = useContext(DragCtx)?.breakpointRef?.current || 'desktop';
  const isMobile = bp === 'mobile';

  const groups = groupByDayLabel(schedule);
  const multiDay = groups.length > 1;

  const [activeKey, setActiveKey] = useState(() => groups[0]?.key || '');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const currentItems = (groups.find(g => g.key === activeKey) || groups[0])?.items || [];
  const empty = schedule.length === 0;

  const handleAdd = () => { setEditingItem(null); setDrawerOpen(true); };
  const handleEdit = (item) => { setEditingItem(item); setDrawerOpen(true); };

  const handleDrawerSave = async (form) => {
    const serializeSpeakers = (spkrs) =>
      (spkrs || []).map(s => (typeof s === 'object' && s !== null ? s.id : s)).filter(Boolean);

    // For new sessions, set dayLabel from the currently active tab
    const activeDayLabel = groups.find(g => g.key === activeKey)?.dayLabel || activeKey || '';

    const merged = {
      ...form,
      id: editingItem?.id || form.id || uuid(),
      dayLabel: editingItem ? (form.dayLabel || editingItem.dayLabel) : (form.dayLabel || activeDayLabel),
      dayNumber: editingItem ? (form.dayNumber || editingItem.dayNumber || 1) : (groups.find(g => g.key === activeKey)?.tabIndex || 1),
      startTime: form.startTime || '',
      endTime: form.endTime || '',
      timeSlots: [{ startTime: form.startTime || '', endTime: form.endTime || '' }],
    };

    const updatedSchedule = editingItem
      ? schedule.map(s => s.id === editingItem.id ? merged : s)
      : [...schedule, merged];

    // Merge session speakers into event.speakers
    const sessionSpeakers = (form.speakers || []).filter(s => s && typeof s === 'object' && s.id);
    const base = speakers || [];
    let updatedSpeakers = base;
    if (sessionSpeakers.length) {
      updatedSpeakers = [
        ...base.map(s => sessionSpeakers.find(sp => sp.id === s.id) || s),
        ...sessionSpeakers.filter(sp => !base.some(b => b.id === sp.id)),
      ];
    }
    const speakersChanged = updatedSpeakers !== base;

    // Serialize schedule for the API (speaker objects → IDs)
    const serializedSchedule = updatedSchedule.map(s => ({
      ...s,
      speakers: serializeSpeakers(s.speakers),
    }));

    const payload = {
      schedule: serializedSchedule,
      ...(speakersChanged && { speakers: updatedSpeakers }),
    };

    // Optimistic local update — keep full speaker objects in local state so
    // re-opening the drawer still shows names/avatars, not bare IDs
    onScheduleUpdate?.(updatedSchedule, speakersChanged ? updatedSpeakers : undefined);
    if (speakersChanged) onSpeakersUpdate?.(updatedSpeakers);

    // Persist to API — same pattern as EventScheduleTab.handleDrawerSave
    if (eventId) {
      try {
        await api.put(`/events/${eventId}`, payload);
      } catch (e) {
        console.warn('ScheduleElement: failed to save session', e);
      }
    }

    setDrawerOpen(false);
    setEditingItem(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const updated = schedule.filter(s => s.id !== deleteTarget.id);
    onScheduleUpdate?.(updated);
    if (eventId) {
      try { await api.put(`/events/${eventId}`, { schedule: updated }); } catch { }
    }
    setDeleteTarget(null);
  };

  return (
    <>
      <Box
        draggable
        onDragStart={e => { applyDragPreview(e, e.currentTarget); onDragStart(e); }}
        onClick={e => { e.stopPropagation(); onSelect(); }}
        onContextMenu={e => {
          e.preventDefault(); e.stopPropagation();
          onSelect();
          openMenu(el.id, e.clientX, e.clientY);
        }}
        sx={{
          position: 'relative', borderRadius: 1, cursor: 'grab', p: '4px',
          outline: selected ? `2px solid ${SCHEDULE_COLOR}` : '2px solid transparent',
          '&:hover': { outline: selected ? undefined : `2px dashed ${alpha(SCHEDULE_COLOR, 0.35)}` },
        }}
      >
        {selected && (
          <Box sx={{
            position: 'absolute', top: -22, left: 0,
            background: SCHEDULE_COLOR, color: '#fff',
            fontSize: '0.65rem', fontWeight: 700, px: 0.75, py: 0.25,
            borderRadius: '4px 4px 0 0', whiteSpace: 'nowrap',
            pointerEvents: 'none', zIndex: 100,
          }}>
            Schedule
          </Box>
        )}

        {empty ? (
          <Box sx={{
            py: 6, textAlign: 'center',
            background: alpha(SCHEDULE_COLOR, 0.04), borderRadius: 2,
            border: `1px dashed ${alpha(SCHEDULE_COLOR, 0.25)}`,
          }}>
            <Typography sx={{ fontSize: '0.82rem', color: alpha(SCHEDULE_COLOR, 0.6), fontWeight: 500 }}>
              No schedule items yet — add them in the Schedule tab
            </Typography>
          </Box>
        ) : (
          <Box>
            {multiDay && (
              <Box sx={{
                display: 'flex', width: '100%',
                borderBottom: `2px solid ${el.dividerColor || '#f0f4f8'}`,
                overflowX: isMobile ? 'auto' : 'visible',
                '&::-webkit-scrollbar': { height: 0 },
                scrollbarWidth: 'none',
              }}>
                {groups.map(group => (
                  <DayTab
                    key={group.key}
                    group={group}
                    active={activeKey === group.key}
                    onClick={e => { e.stopPropagation(); setActiveKey(group.key); }}
                    totalTabs={groups.length}
                    primaryColor={el.primaryColor || SCHEDULE_COLOR}
                    isMobile={isMobile}
                  />
                ))}
              </Box>
            )}
            <Box>
              {currentItems.map((item, idx) => (
                <ScheduleRow
                  key={item.id || idx}
                  item={item}
                  el={el}
                  allSpeakers={speakers}
                  bannerUrl={eventBannerUrl}
                  isLast={idx === currentItems.length - 1}
                  onEdit={handleEdit}
                  onDelete={item => setDeleteTarget(item)}
                  isMobile={isMobile}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* ── Add schedule FAB — visible only when selected ── */}
        {selected && (
          <Box
            onClick={e => { e.stopPropagation(); handleAdd(); }}
            sx={{
              position: 'absolute', bottom: 12, right: 12, zIndex: 20,
              height: 40, borderRadius: '999px', px: 2, gap: 0.75,
              background: SCHEDULE_COLOR, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 14px ${alpha(SCHEDULE_COLOR, 0.45)}`,
              cursor: 'pointer', transition: 'all 0.18s',
              '&:hover': { background: '#0d6b62', transform: 'scale(1.05)', boxShadow: `0 6px 20px ${alpha(SCHEDULE_COLOR, 0.55)}` },
            }}
          >
            <AddOutlined sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
              Add schedule
            </Typography>
          </Box>
        )}
      </Box>

      {/* SessionDrawer */}
      <SessionDrawer
        open={drawerOpen}
        session={editingItem}
        event={{ speakers }}
        onClose={() => { setDrawerOpen(false); setEditingItem(null); }}
        onSave={handleDrawerSave}
        onDelete={editingItem ? () => { setDeleteTarget(editingItem); setDrawerOpen(false); } : null}
        hideEdit
      />

      {/* Delete confirmation */}
      {deleteTarget && (
        <Dialog open onClose={() => setDeleteTarget(null)} onClick={e => e.stopPropagation()}
          PaperProps={{ sx: { borderRadius: 3, maxWidth: 400, width: '100%', p: 0.5 } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 0.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, background: alpha('#d32f2f', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <WarningAmberOutlined sx={{ fontSize: 18, color: '#d32f2f' }} />
            </Box>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e' }}>Delete session?</Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 0.5 }}>
            <Typography sx={{ fontSize: '0.85rem', color: '#5a6474' }}>
              <strong>{deleteTarget.title}</strong> will be permanently removed from the schedule.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
            <Button onClick={() => setDeleteTarget(null)} variant="outlined" size="small"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#e4e9f0', color: '#5a6474' }}>
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} variant="contained" size="small" color="error"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

